import { useState } from "react";
import { useTranslation } from "react-i18next";
import { mockVolunteerOpportunities, mockTestimonials } from "@/mocks/volunteers";

const VOLUNTEER_FORM_URL = "https://readdy.ai/api/form/d7ihj4ehdlg6dog9dev0";

interface ApplyModalProps {
  opportunity: typeof mockVolunteerOpportunities[0];
  onClose: () => void;
}

function ApplyModal({ opportunity, onClose }: ApplyModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        message: form.message,
        opportunity: opportunity.title,
      });
      await fetch(VOLUNTEER_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        {submitted ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#4ECDC4]/20 text-[#4ECDC4] mx-auto mb-4">
              <i className="ri-check-line text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-[#1B2838] mb-2">{t("volunteerPage.apply.submitted")}</h3>
            <p className="text-gray-500 text-sm mb-6">{t("volunteerPage.apply.submittedDesc")}</p>
            <button onClick={onClose} className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full font-semibold text-sm cursor-pointer">
              {t("volunteerPage.apply.close")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-[#1B2838]">{t("volunteerPage.apply.title")}</h3>
                <p className="text-[#4ECDC4] text-sm mt-1">{opportunity.title}</p>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 cursor-pointer hover:bg-gray-200">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("volunteerPage.form.name")}</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                  placeholder={t("volunteerPage.form.namePlaceholder")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("volunteerPage.form.email")}</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                  placeholder={t("volunteerPage.form.emailPlaceholder")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("volunteerPage.form.message")}</label>
                <textarea
                  name="message"
                  required
                  value={form.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setForm({ ...form, message: e.target.value });
                  }}
                  rows={4}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none"
                  placeholder={t("volunteerPage.form.messagePlaceholder")}
                />
                <p className="text-xs text-gray-400 text-right mt-1">{form.message.length}/500</p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-[#4ECDC4] text-[#1B2838] rounded-full font-bold text-sm cursor-pointer hover:bg-[#3dbdb5] transition-colors disabled:opacity-60"
              >
                {submitting ? t("volunteerPage.form.submitting") : t("volunteerPage.form.submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function VolunteerPage() {
  const { t } = useTranslation();
  const [selectedOpp, setSelectedOpp] = useState<typeof mockVolunteerOpportunities[0] | null>(null);

  return (
    <main className="pt-24">
      {/* Header */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20volunteers%20working%20together%20on%20a%20community%20project%2C%20smiling%20and%20collaborative%2C%20warm%20natural%20lighting%2C%20outdoor%20setting%20with%20greenery&width=1600&height=600&seq=vol_hero&orientation=landscape"
            alt="Volunteer"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#1B2838]/75"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-4 block">{t("volunteerPage.tag")}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("volunteerPage.title")}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {t("volunteerPage.subtitle")}
          </p>
        </div>
      </section>

      {/* Opportunities */}
      <section className="py-16 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">{t("volunteerPage.positions.tag")}</span>
            <h2 className="text-3xl font-bold text-[#1B2838]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("volunteerPage.positions.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {mockVolunteerOpportunities.map((opp) => (
              <div
                key={opp.id}
                className="bg-white rounded-xl border border-gray-100 p-6 hover:border-[#4ECDC4]/40 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="font-bold text-[#1B2838] text-lg leading-tight">{opp.title}</h3>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full whitespace-nowrap">{opp.commitment}</span>
                    <span className="text-xs bg-[#4ECDC4]/10 text-[#4ECDC4] px-2.5 py-1 rounded-full whitespace-nowrap">{opp.location}</span>
                  </div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{opp.description}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {opp.skills.map((skill) => (
                    <span key={skill} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    <i className="ri-user-line mr-1"></i>
                    {opp.spots} {t("volunteerPage.spotsAvailable")}
                  </span>
                  <button
                    onClick={() => setSelectedOpp(opp)}
                    className="px-5 py-2 bg-[#1B2838] text-white rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#2a3a4a] transition-colors"
                  >
                    {t("volunteerPage.applyNow")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">{t("volunteerPage.stories.tag")}</span>
            <h2 className="text-3xl font-bold text-[#1B2838]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("volunteerPage.stories.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {mockTestimonials.map((tItem) => (
              <div key={tItem.id} className="bg-[#FAF8F5] rounded-xl p-6 border border-gray-100">
                <i className="ri-double-quotes-l text-3xl text-[#4ECDC4]/40 mb-3 block"></i>
                <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">&ldquo;{tItem.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <img src={tItem.avatar} alt={tItem.name} className="w-10 h-10 rounded-full object-cover object-top" />
                  <div>
                    <div className="font-semibold text-[#1B2838] text-sm">{tItem.name}</div>
                    <div className="text-gray-400 text-xs">{tItem.role} &bull; {tItem.duration}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedOpp && <ApplyModal opportunity={selectedOpp} onClose={() => setSelectedOpp(null)} />}
    </main>
  );
}